import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useClinicPhotosSection } from '../hooks/useClinicPhotosSection';
import { getBestImageUrl } from '../utils/imageUrl';
import { getCurrentLocale } from '../services/api';

function ClinicPhotosSection(): React.JSX.Element {
  const locale = getCurrentLocale();
  const { data, loading } = useClinicPhotosSection();
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  // Handle animation when element comes into view
  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated]);

  // Handle case when element is already in view on page load
  useEffect(() => {
    if (!loading && !hasAnimated && sectionRef.current) {
      const rect = sectionRef.current.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible) {
        // Small delay to ensure the component is fully rendered
        const timer = setTimeout(() => {
          setHasAnimated(true);
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
  }, [loading, hasAnimated]);

  // Debug logging for clinic photos section data
  if (data && !loading) {
    console.log('🏥 ClinicPhotosSection - ✅ Strapi data loaded:', !!data);
    console.log('🏥 ClinicPhotosSection - Data keys:', Object.keys(data || {}));
    console.log('🏥 ClinicPhotosSection - Images count:', data.images?.length || 0);
    console.log('🏥 ClinicPhotosSection - Title:', data.title);
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  // Show loading state
  if (loading) {
    return (
      <motion.section
        ref={sectionRef}
        className="clinic-photos"
        variants={containerVariants}
        initial="hidden"
        animate={hasAnimated ? "visible" : "hidden"}
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="clinic-photos-grid">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading clinic photos...</div>
        </div>
      </motion.section>
    );
  }

  // Extract data from Strapi
  const title = data?.title || 'Onze Kliniek';
  const images = data?.images || [];

  return (
    <motion.section
      ref={sectionRef}
      className="clinic-photos"
      variants={containerVariants}
      initial="hidden"
      animate={hasAnimated ? "visible" : "hidden"}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      {title && (
        <motion.div className="clinic-photos-header" variants={itemVariants}>
          <h2 className="clinic-photos-title">{title}</h2>
        </motion.div>
      )}
      
      <div className="clinic-photos-grid">
        {images.length > 0 ? (
          images.map((image, index) => {
            // Use getBestImageUrl for optimal image quality
            const imageUrl = getBestImageUrl(image);
            const altText = image.alternativeText || image.caption || `Clinic photo ${index + 1}`;
            
            // Skip images without valid image data
            if (!imageUrl) {
              console.log(`⚠️ Clinic photo ${index + 1} - No image URL available`);
              return null;
            }
            
            console.log(`🖼️ Clinic photo ${index + 1} - Using image: ${imageUrl.split('/').pop()}`);
            
            return (
              <motion.div key={image.id || index} className="clinic-photo" variants={itemVariants}>
                <img 
                  src={imageUrl} 
                  alt={altText} 
                />
              </motion.div>
            );
          })
        ) : (
          <div className="loading-placeholder">
            <div className="loading-title">No clinic photos available</div>
          </div>
        )}
      </div>
    </motion.section>
  );
}

export default ClinicPhotosSection;